import React from 'react';
import './AnatomicalToothButton.css';
import { InternalAnatomy, OcclusalInternal } from './ToothInternalAnatomy';
import ToothConditionOverlay from './ToothConditionOverlay';

const CONDITION_LABELS = {
  higido: 'Hígido', carie: 'Cárie', restauracao: 'Restauração', coroa: 'Coroa',
  implante: 'Implante', ausente: 'Ausente', extracao_indicada: 'Extração indicada',
  extracao_realizada: 'Extração realizada', canal: 'Canal', fratura: 'Fratura',
  protese: 'Prótese', selante: 'Selante', outro: 'Outro',
};

const FACE_LABELS = {
  dente: 'Dente inteiro', oclusal: 'Face oclusal', incisal: 'Face incisal',
  mesial: 'Face mesial', distal: 'Face distal', vestibular: 'Face vestibular',
  lingual: 'Face lingual', palatina: 'Face palatina', raiz: 'Raiz',
};

const toothKind = (number) => {
  const position = Number(String(number).slice(-1));
  if (position <= 2) return 'incisor';
  if (position === 3) return 'canine';
  if (position <= 5) return 'premolar';
  return 'molar';
};

const isUpperTooth = (number) => ['1', '2', '5', '6'].includes(String(number)[0]);
const isLeftTooth = (number) => ['2', '3', '6', '7'].includes(String(number)[0]);

function Roots({ kind, upper }) {
  if (kind === 'molar' && upper) {
    return <><path d="M35 72 C32 91 22 111 19 143 C18 153 24 158 29 148 C39 127 45 103 49 79Z"/><path d="M51 76 C52 99 51 124 55 151 C57 160 64 158 66 149 C69 123 66 96 65 75Z"/><path d="M65 77 C74 98 81 119 89 143 C93 154 100 151 98 140 C95 113 86 91 79 70Z"/></>;
  }
  if (kind === 'molar') {
    return <><path d="M35 73 C35 96 25 122 26 149 C27 159 34 158 38 149 C48 125 50 99 51 76Z"/><path d="M62 76 C65 101 68 128 79 150 C83 159 90 156 89 146 C87 118 78 93 77 71Z"/></>;
  }
  if (kind === 'premolar' && upper) {
    return <><path d="M41 72 C41 95 34 119 35 149 C36 158 43 158 47 148 C53 125 54 99 55 73Z"/><path d="M57 73 C59 99 60 126 67 149 C70 158 77 157 77 147 C76 119 69 94 68 70Z"/></>;
  }
  const width = kind === 'canine' ? 23 : kind === 'premolar' ? 20 : 17;
  const length = kind === 'canine' ? 158 : 148;
  return <path d={`M${50-width/2} 70 C${50-width/2-2} 96 ${43} 122 46 ${length-8} C48 ${length+5} 55 ${length+5} 58 ${length-8} C62 121 ${50+width/2+2} 95 ${50+width/2} 70Z`}/>;
}

function CrownFront({ kind }) {
  if (kind === 'incisor') return <path d="M31 20 Q50 11 69 20 L72 51 Q70 70 59 78 Q50 84 41 78 Q30 70 28 51Z"/>;
  if (kind === 'canine') return <path d="M31 32 Q34 18 47 16 L50 7 L54 16 Q67 19 70 33 L69 57 Q65 73 56 79 Q50 84 43 78 Q34 70 30 56Z"/>;
  if (kind === 'premolar') return <path d="M25 32 Q28 18 41 20 Q50 9 58 20 Q72 18 76 32 L74 58 Q70 75 57 80 Q49 84 40 79 Q28 73 25 57Z"/>;
  return <path d="M18 32 Q21 16 36 20 Q44 8 51 19 Q61 8 68 20 Q82 17 84 33 L82 59 Q78 75 65 80 Q50 86 35 80 Q21 75 18 58Z"/>;
}

function OcclusalCrown({ kind }) {
  if (kind === 'incisor') return <><path d="M26 20 Q50 10 74 20 Q70 55 50 59 Q30 55 26 20Z"/><path className="tooth-detail" d="M32 25 Q50 34 68 25"/></>;
  if (kind === 'canine') return <><path d="M50 8 L75 32 L50 60 L25 32Z"/><path className="tooth-detail" d="M50 15 L50 52 M32 32 L68 32"/></>;
  if (kind === 'premolar') return <><ellipse cx="50" cy="34" rx="29" ry="25"/><ellipse className="tooth-cusp" cx="38" cy="34" rx="10" ry="15"/><ellipse className="tooth-cusp" cx="62" cy="34" rx="10" ry="15"/><path className="tooth-groove" d="M50 15 C45 27 55 41 50 55"/></>;
  return <><path d="M20 19 Q20 9 31 8 L70 8 Q81 9 81 20 L81 49 Q79 60 68 61 L31 61 Q19 59 19 48Z"/><circle className="tooth-cusp" cx="34" cy="23" r="10"/><circle className="tooth-cusp" cx="66" cy="23" r="10"/><circle className="tooth-cusp" cx="34" cy="47" r="10"/><circle className="tooth-cusp" cx="66" cy="47" r="10"/><path className="tooth-groove" d="M24 34 H76 M50 12 V56"/></>;
}

export default function AnatomicalToothButton({ number, entry, conditionLabel, conditionColor, onClick, view = 'both', showLabel = true }) {
  const kind = toothKind(number);
  const upper = isUpperTooth(number);
  const mirrored = isLeftTooth(number);
  const records = entry?.records || (entry ? [entry] : []);
  const isMissing = records.some(item => ['ausente', 'extracao_realizada'].includes(item.condicao));
  const legend = records.length
    ? records.map(item => `${CONDITION_LABELS[item.condicao] || item.condicao} · ${FACE_LABELS[item.face] || item.face}${item.observacao ? ` — ${item.observacao}` : ''}`).join('\n')
    : 'Sem registro clínico';
  const accessibleLabel = `Dente ${number}. ${legend.replaceAll('\n', '. ')}`;
  const statusColor = entry ? conditionColor : '#c8d1d9';
  const clinicalTint = entry ? conditionColor : '#d8caa4';
  const gradientId = `tooth-${number}-${view}`;
  const transform = `${upper ? 'translate(0 170) scale(1 -1)' : ''} ${mirrored ? 'translate(100 0) scale(-1 1)' : ''}`.trim();

  return (
    <button type="button" className={`anatomical-tooth anatomical-tooth--${kind} anatomical-tooth--${view}${isMissing ? ' anatomical-tooth--missing' : ''}`} onClick={onClick} style={{ '--tooth-status': statusColor }} aria-label={accessibleLabel} data-tooth-number={number} data-tooth-legend={legend}>
      {showLabel && <strong>{number}</strong>}
      {view !== 'occlusal' && <svg className="anatomical-tooth__front" viewBox="0 0 100 170" role="img" aria-hidden="true">
        <defs>
          <linearGradient id={`${gradientId}-root`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#aa956d"/><stop offset="0.16" stopColor="#d5c195"/><stop offset="0.46" stopColor="#f3e4bd"/><stop offset="0.68" stopColor="#ddc99d"/><stop offset="1" stopColor="#9b835e"/>
          </linearGradient>
          <radialGradient id={`${gradientId}-enamel`} cx="36%" cy="24%" r="78%">
            <stop offset="0" stopColor="#fffef5"/><stop offset="0.28" stopColor="#fff9df"/><stop offset="0.64" stopColor="#eadcb8"/><stop offset="0.86" stopColor={clinicalTint} stopOpacity={entry ? '.48' : '.42'}/><stop offset="1" stopColor="#a99570"/>
          </radialGradient>
          <linearGradient id={`${gradientId}-gloss`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#fff" stopOpacity=".88"/><stop offset=".42" stopColor="#fff" stopOpacity=".12"/><stop offset="1" stopColor="#735f3f" stopOpacity=".28"/>
          </linearGradient>
          <filter id={`${gradientId}-shadow`} x="-30%" y="-20%" width="160%" height="160%">
            <feDropShadow dx="1.5" dy="2.4" stdDeviation="1.8" floodColor="#5e4f38" floodOpacity=".28"/>
          </filter>
        </defs>
        <g transform={transform} className="tooth-root" style={{fill:`url(#${gradientId}-root)`}}><Roots kind={kind} upper={upper}/></g>
        <g transform={transform} className="tooth-crown tooth-crown--volume" style={{fill:`url(#${gradientId}-enamel)`,filter:`url(#${gradientId}-shadow)`}}><CrownFront kind={kind}/></g>
        <g transform={transform}><InternalAnatomy kind={kind} upper={upper}/></g>
        <g transform={transform} className="tooth-crown tooth-crown--gloss" style={{fill:`url(#${gradientId}-gloss)`}}><CrownFront kind={kind}/></g>
        <path className="tooth-neck-line" d={upper ? 'M30 68 Q50 76 70 68' : 'M30 102 Q50 94 70 102'}/>
        <ToothConditionOverlay records={records} view="front" transform={transform}/>
      </svg>}
      {view !== 'front' && <svg className="anatomical-tooth__occlusal" viewBox="0 0 100 70" role="img" aria-hidden="true">
        <defs>
          <radialGradient id={`${gradientId}-occlusal`} cx="38%" cy="30%" r="72%">
            <stop offset="0" stopColor="#fffef4"/><stop offset=".48" stopColor="#f4e8c9"/><stop offset=".76" stopColor="#d6c39b"/><stop offset="1" stopColor="#8f7a59"/>
          </radialGradient>
          <filter id={`${gradientId}-occlusal-shadow`} x="-25%" y="-25%" width="150%" height="150%"><feDropShadow dx="1" dy="2" stdDeviation="1.5" floodColor="#65563e" floodOpacity=".3"/></filter>
        </defs>
        <g className="tooth-crown tooth-crown--occlusal" style={{fill:`url(#${gradientId}-occlusal)`,filter:`url(#${gradientId}-occlusal-shadow)`}}><OcclusalCrown kind={kind}/></g>
        <OcclusalInternal kind={kind}/>
        <ToothConditionOverlay records={records} view="occlusal"/>
      </svg>}
      {showLabel && <small>{entry ? conditionLabel : 'Sem registro'}</small>}
    </button>
  );
}
